import React, { useEffect, useState } from 'react';
import MetricCard from '../components/metric-card';
import SpendAnalyticsCard from '../components/spend-analytics-card';
import MenuBar from '../components/menu-bar';
import { AggregateSourceData, PlatformData } from '../types/performance.types';
import { useGetOverviewAggregateDataQuery, useGetOverviewTimeSeriesDataQuery } from '../endpoints/overview.endpoints';
import { useGetPlatformsQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import { formatDisplayDate } from 'src/helpers/date.helpers';
import { calculatePercentChange } from 'src/pages/reports/utils/calculated-values.utils';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/redux-store';
import { getPlatformMargin } from '../utils/platform-margin';

const colors = ['bg-yellow-500', 'bg-blue-500', 'bg-green-501', 'bg-red-502', 'bg-purple-500', 'bg-orange-500', 'bg-black', 'bg-gray-600', 'bg-green-1000' ];
const chartColors = ['#F9AB00', '#3257FB', '#1e8e3c', '#d93026', '#934DFF', '#F25A18', 'black', 'gray', 'green' ];

export type DateFilterType = 'last_month' | 'this_month' | 'last_week' | 'last_quarter' | 'last_3_days' | 'yesterday' | 'year_to_date' | 'custom';

const getInitialDates = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const start = new Date(currentYear, currentMonth, 1);
  const end = now;

  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start: formatDateLocal(start),
    end: formatDateLocal(end),
  };
};

const OverviewTab: React.FC = () => {
  const [selectedMetric, setSelectedMetric] = useState<string>('Spend');
  const [dateFilter, setDateFilter] = useState<DateFilterType>('this_month');
  const [editMode, setEditMode] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const activeClient = useSelector((state: RootState) => state.userState.activeClient);

  const [startDate, setStartDate] = useState<string>(getInitialDates().start);
  const [endDate, setEndDate] = useState<string>(getInitialDates().end);

  const [activePlatforms, setActivePlatforms] = useState<Record<string, PlatformData>>({});
  const [totalSpend, setTotalSpend] = useState<number>(0);
  const [totalSpendChange, setTotalSpendChange] = useState<number>(0);
  const [totalMargin, setTotalMargin] = useState<number>(0);

  const {data: platforms, isLoading: isLoadingPlatforms, error: platformsError, refetch: refetchPlatforms} = useGetPlatformsQuery({previewClientId: activeClient?.ext_client_id});
  const {data: timeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError, refetch: refetchTimeSeries} = useGetOverviewTimeSeriesDataQuery({
    start_date: startDate,
    end_date: endDate,
    extClientId: activeClient?.ext_client_id,
  });
  const {data: aggregateData, isLoading: isLoadingAggregate, error: aggregateError} = useGetOverviewAggregateDataQuery({
    start_date: startDate,
    end_date: endDate,
    filter: dateFilter,
    extClientId: activeClient?.ext_client_id,
  });

  useEffect(() => {
    setIsLoading(true);
  }, [startDate, endDate]);

  useEffect(() => {
    if (platforms && aggregateData && timeSeriesData) {
      const _platforms: Record<string, PlatformData> = {};
      platforms.forEach((platform: string, index: number) => {
        _platforms[platform] = {
          name: platform,
          displayName: platform.replace('_', ' '),
          past: aggregateData?.sources[platform]?.past as unknown as Record<string, AggregateSourceData>,
          dif: aggregateData?.sources[platform]?.dif as unknown as Record<string, AggregateSourceData>,
          aggregateData: aggregateData?.sources[platform]?.current as unknown as Record<string, AggregateSourceData>,
          timeSeriesData: timeSeriesData?.sources[platform] as unknown as Record<string, AggregateSourceData[]>,
          color: colors[index],
          chartColor: chartColors[index],
          enabled: activePlatforms[platform]?.enabled ?? true, // Preserve existing enabled state
          margin: getPlatformMargin(platform),
        };
      });
      setActivePlatforms(_platforms);
      setIsLoading(false);
    }
  }, [platforms, aggregateData, timeSeriesData]);

  useEffect(() => {
    calculateTotalSpend();
    calculateTotalSpendChange();
  }, [aggregateData, startDate, endDate, activePlatforms]);

  const calculateTotalSpend = () => {
    if (activePlatforms) {
      const totalSpendAmount = Object.values(activePlatforms)
        .filter((p): p is PlatformData => p.enabled)
        .reduce((acc, p) => acc + Number((p.aggregateData?.spend || 0)) * (1 + p.margin / 100), 0);

      setTotalSpend(totalSpendAmount);
    }
  };

  const calculateTotalSpendChange = () => {
    if (activePlatforms) {
      const currentSpendAmount = Object.values(activePlatforms)
        .filter((p): p is PlatformData => p.enabled)
        .reduce((acc, p) => acc + Number((p.aggregateData?.spend || 0)), 0);

      const pastSpendAmount = Object.values(activePlatforms)
        .filter((p): p is PlatformData => p.enabled)
        .reduce((acc, p) => acc + Number((p.past?.spend || 0)), 0);

      setTotalSpendChange(calculatePercentChange(currentSpendAmount, pastSpendAmount));
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleMergeToggle = () => {
    setMergeMode(!mergeMode);
  };

  const handleSelectionChange = (selection: string) => {
    setSelectedMetric(selection);
  };

  // Helper function to format dates in local timezone (YYYY-MM-DD)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (dateFilter: string, _startDate?: string, _endDate?: string) => {
    setDateFilter(dateFilter as DateFilterType);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    switch (dateFilter) {
      case 'last_month':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth - 1, 1)));
        setEndDate(formatDateLocal(new Date(currentYear, currentMonth, 0)));
        break;

      case 'this_month':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth, 1)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'last_week':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth, currentDate - 8)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'last_quarter':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth - 3, 1)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'last_3_days':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth, currentDate - 4)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'yesterday':
        setStartDate(formatDateLocal(new Date(currentYear, currentMonth, currentDate - 2)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'year_to_date':
        setStartDate(formatDateLocal(new Date(currentYear, 0, 1)));
        setEndDate(formatDateLocal(yesterday));
        break;

      case 'custom':
        if (_startDate && _endDate) {
          setStartDate(_startDate);
          setEndDate(_endDate);
        }
        break;

      default:
        setStartDate(_startDate || formatDateLocal(new Date(currentYear, currentMonth - 1, 1)));
        setEndDate(_endDate || formatDateLocal(new Date(currentYear, currentMonth, 0)));
        break;
    }
  };

  const handlePlatformClick = (platform: PlatformData) => {
    setActivePlatforms(prev => {
      const newPlatforms = { ...prev };
      newPlatforms[platform.name] = { ...platform};
      return newPlatforms;
    });
  };

  const formatDateRange = () => {
    return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
  };

  return (
    <div className="h-full flex flex-col space-y-4 p-6 pt-0">
      <div className="flex-1 space-y-6">

        <MenuBar
          editMode={editMode}
          mergeMode={mergeMode}
          selectedMetric={selectedMetric}
          selectedDate={dateFilter}
          onDateChange={handleDateChange}
          onSelectionChange={handleSelectionChange}
          onEditToggle={handleEditToggle}
          onMergeToggle={handleMergeToggle}
          className='bg-gray-201 text-black rounded-full'
        />

        {/* Spend Card - Full Width */}
        <SpendAnalyticsCard
          isLoading={isLoading || isLoadingAggregate || isLoadingTimeSeries || isLoadingPlatforms}
          aggregateData={aggregateData?.sources || {}}
          timeSeriesData={timeSeriesData?.sources || {}}
          editMode={editMode}
          mergeMode={mergeMode}
          selectedMetric={selectedMetric}
          startDate={startDate}
          endDate={endDate}
          handlePlatformClick={handlePlatformClick}
          platforms={activePlatforms}
        />

        {/* Metrics */}
        <div className='flex gap-4'>
          <MetricCard
            className=''
            title="Total Spend"
            value={totalSpend}
            percentChange={totalSpendChange}
            type='currency'
            subtext={formatDateRange()}
          />
        </div>
      </div>

    </div>
  );
};

export default OverviewTab;
